import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const VisualClockPicker = ({ value, onChange, inputClass, placeholder }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('hours'); // 'hours' or 'minutes'
  const [hours, setHours] = useState(9);
  const [minutes, setMinutes] = useState(0);
  const clockRef = useRef(null);
  const isDragging = useRef(false);
  const hoursRef = useRef(9);
  const minutesRef = useRef(0);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m)) {
        setHours(h);
        setMinutes(m);
        hoursRef.current = h;
        minutesRef.current = m;
      }
    }
  }, [value]);

  // Get coordinates on clock face
  // Angle: 0 = top (12 o'clock), 90 = right (3 o'clock), 180 = bottom (6 o'clock), 270 = left (9 o'clock)
  // getAngle already returns angle with 0 at top, so we need to convert to CSS coordinates
  // CSS: 0° = right, 90° = bottom, so we subtract 90 to align with clock
  const getCoordinates = (angle, radius) => {
    const rad = (angle - 90) * (Math.PI / 180); // Convert to radians, rotate so 0° is at top
    return {
      x: 50 + radius * Math.cos(rad),
      y: 50 + radius * Math.sin(rad)
    };
  };

  // Calculate distance from center
  const getDistance = (x, y, centerX, centerY) => {
    return Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
  };

  // Calculate angle from center
  const getAngle = (x, y, centerX, centerY) => {
    const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI);
    // Normalize to 0-360, starting from top (0 degrees = 12 o'clock)
    let normalizedAngle = (angle + 90 + 360) % 360;
    return normalizedAngle;
  };

  // Handle clock face click/touch
  const handleClockClick = (e) => {
    if (!clockRef.current) return;
    
    const rect = clockRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const distance = getDistance(clientX, clientY, centerX, centerY);
    const angle = getAngle(clientX, clientY, centerX, centerY);
    const radius = rect.width / 2;
    const normalizedDistance = distance / radius;

    if (mode === 'hours') {
      // 24-hour format: calculate which hour (0-23)
      // Angle is 0-360 starting from top (12 o'clock = 0)
      // Each hour is 15 degrees (360/24)
      // Use round with small offset to center selection
      let hour24 = Math.round(angle / 15) % 24;
      if (hour24 < 0) hour24 = 23;
      
      setHours(hour24);
      hoursRef.current = hour24;
      updateTime(hour24, minutesRef.current);
      
      // Automatically switch to minutes after selecting hour
      setTimeout(() => {
        setMode('minutes');
      }, 300);
    } else if (mode === 'minutes') {
      // Minutes: calculate which minute (0-59)
      // Angle is 0-360 starting from top (12 o'clock = 0)
      // Each minute is 6 degrees (360/60)
      // Use round for better precision
      let minute = Math.round(angle / 6) % 60;
      if (minute < 0) minute = 59;
      
      setMinutes(minute);
      minutesRef.current = minute;
      updateTime(hoursRef.current, minute);
    }
  };

  // Handle mouse/touch move (for dragging)
  const handleMove = (e) => {
    if (!isDragging.current || !clockRef.current) return;

    const rect = clockRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const angle = getAngle(clientX, clientY, centerX, centerY);

    if (mode === 'hours') {
      let hour24 = Math.round(angle / 15) % 24;
      if (hour24 < 0) hour24 = 23;
      
      if (hour24 !== hoursRef.current) {
        setHours(hour24);
        hoursRef.current = hour24;
        updateTime(hour24, minutesRef.current);
      }
    } else if (mode === 'minutes') {
      let minute = Math.round(angle / 6) % 60;
      if (minute < 0) minute = 59;
      
      if (minute !== minutesRef.current) {
        setMinutes(minute);
        minutesRef.current = minute;
        updateTime(hoursRef.current, minute);
      }
    }
  };

  // Handle mouse/touch start
  const handleStart = (e) => {
    e.preventDefault();
    isDragging.current = true;
    handleClockClick(e);
  };

  // Handle mouse/touch end
  const handleEnd = () => {
    isDragging.current = false;
  };

  // Update time and call onChange
  const updateTime = (h, m) => {
    const formattedTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    onChange(formattedTime);
    hoursRef.current = h;
    minutesRef.current = m;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === '') {
      onChange('');
      return;
    }
    
    let cleaned = inputValue.replace(/[^\d:]/g, '');
    
    if (cleaned.includes(':')) {
      const [h, m] = cleaned.split(':').map(Number);
      if (!isNaN(h) && h >= 0 && h <= 23 && !isNaN(m) && m >= 0 && m <= 59) {
        setHours(h);
        setMinutes(m);
        updateTime(h, m);
        setMode('minutes'); // Switch to minutes view
      }
    }
  };

  // Set up event listeners
  useEffect(() => {
    if (isOpen) {
      const moveHandler = (e) => handleMove(e);
      const endHandler = () => handleEnd();
      
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', endHandler);
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', endHandler);
      
      return () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', endHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', endHandler);
      };
    }
  }, [isOpen, mode]);

  // Calculate selected value angle for indicator
  const getSelectedAngle = () => {
    if (mode === 'hours') {
      return (hours * 15) % 360; // 15 degrees per hour (360/24)
    } else {
      return (minutes * 6) % 360; // 6 degrees per minute (360/60)
    }
  };

  const selectedAngle = getSelectedAngle();
  const selectedCoords = getCoordinates(selectedAngle, 40);

  // Render hour markers (0-23)
  const renderHourMarkers = () => {
    return Array.from({ length: 24 }).map((_, index) => {
      const angle = index * 15; // 15 degrees per hour, 0 starts at top
      const coords = getCoordinates(angle, 42);
      const isSelected = mode === 'hours' && hours === index;
      
      return (
        <div
          key={index}
          className={`absolute text-white font-semibold transition-all ${
            isSelected ? 'text-lg text-blue-400 font-bold' : 'text-sm'
          }`}
          style={{
            left: `${coords.x}%`,
            top: `${coords.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {index}
        </div>
      );
    });
  };

  // Render minute markers (0-59, but show only every 5 minutes)
  const renderMinuteMarkers = () => {
    return Array.from({ length: 60 }).map((_, index) => {
      if (index % 5 !== 0) return null; // Show only every 5 minutes
      
      const angle = index * 6; // 6 degrees per minute, 0 starts at top
      const coords = getCoordinates(angle, 42);
      const isSelected = mode === 'minutes' && minutes === index;
      
      return (
        <div
          key={index}
          className={`absolute text-white font-semibold transition-all ${
            isSelected ? 'text-lg text-green-400 font-bold' : 'text-sm'
          }`}
          style={{
            left: `${coords.x}%`,
            top: `${coords.y}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {String(index).padStart(2, '0')}
        </div>
      );
    });
  };

  return (
    <div className="relative">
      {/* Input field */}
      <input
        type="text"
        value={value || ''}
        // On mobilnom telefonu ne želimo da iskače tastatura,
        // vreme se bira preko vizuelnog sata.
        readOnly
        inputMode="none"
        autoComplete="off"
        onFocus={() => {
          setIsOpen(true);
          setMode('hours'); // Always start with hours
        }}
        onClick={() => {
          setIsOpen(true);
          setMode('hours');
        }}
        placeholder={placeholder || 'HH:MM'}
        className={inputClass}
        maxLength={5}
      />

      {/* Clock popup */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-800 rounded-lg shadow-xl p-4 border border-gray-700 w-[calc(100vw-2rem)] max-w-sm sm:w-96">
            {/* Mode indicator */}
            <div className="flex justify-center items-center mb-4 space-x-4">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMode('hours');
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  mode === 'hours'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {t('jobs.hours') || 'Sati'}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMode('minutes');
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  mode === 'minutes'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {t('jobs.minutes') || 'Minute'}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="p-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                title={t('common.close') || 'Zatvori'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Clock face */}
            <div
              ref={clockRef}
              className="relative w-56 h-56 sm:w-64 sm:h-64 mx-auto bg-gray-900 rounded-full border-4 border-gray-700 cursor-pointer"
              style={{ touchAction: 'none' }}
              onMouseDown={handleStart}
              onTouchStart={handleStart}
            >
              {/* Hour markers (0-23) */}
              {mode === 'hours' && renderHourMarkers()}

              {/* Minute markers (0-59) */}
              {mode === 'minutes' && renderMinuteMarkers()}

              {/* Selected indicator line */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ zIndex: 2 }}
              >
                <line
                  x1="50%"
                  y1="50%"
                  x2={`${selectedCoords.x}%`}
                  y2={`${selectedCoords.y}%`}
                  stroke={mode === 'hours' ? '#60A5FA' : '#34D399'}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>

              {/* Selected value indicator dot */}
              <div
                className={`absolute w-6 h-6 rounded-full ${
                  mode === 'hours' ? 'bg-blue-500' : 'bg-green-500'
                }`}
                style={{
                  left: `${selectedCoords.x}%`,
                  top: `${selectedCoords.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 3,
                  boxShadow: '0 0 10px rgba(96, 165, 250, 0.5)'
                }}
              />

              {/* Center dot */}
              <div className="absolute w-4 h-4 bg-white rounded-full" style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 4
              }} />
            </div>

            {/* Time display */}
            <div className="mt-4 text-center">
              <div className="text-3xl font-bold text-white mb-2">
                {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
              </div>
              <div className="text-sm text-gray-400">
                {mode === 'hours' 
                  ? (t('jobs.selectHour') || 'Izaberite sat (0-23)')
                  : (t('jobs.selectMinute') || 'Izaberite minute (0-59)')
                }
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="mt-4 flex space-x-2">
              {mode === 'minutes' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMode('hours');
                  }}
                  className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  {t('common.back') || 'Nazad'}
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className={`py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors ${
                  mode === 'hours' ? 'w-full' : 'flex-1'
                }`}
              >
                {t('common.confirm') || 'Potvrdi'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisualClockPicker;
